import {
  AlertCircle,
  CheckCircle2,
  Coins,
  CreditCard,
  TrendingDown,
  TrendingUp,
  Wallet,
} from "lucide-react";
import type React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import type { TransactionType } from "@/models/point-transaction";

interface TransactionSummaryProps {
  transactionType: TransactionType;
  isPurchaseType: boolean;
  className?: string;
  pointsToEarn: number;
  pointsToRewards: number;
  pointsToSpend: number;
  cashValue?: string;
  coveredValue?: string;
  purchaseValue?: string;
  points: number;
}

function SectionHeader({ title }: { title: string }) {
  return (
    <div className="flex items-center gap-3 pb-3 pt-6 first:pt-0">
      <h3 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {title}
      </h3>
      <div className="h-px flex-1 bg-border" />
    </div>
  );
}

function InfoRow({
  icon: Icon,
  label,
  value,
  valueClassName,
}: {
  icon: React.ElementType;
  label: string;
  value: React.ReactNode;
  valueClassName?: string;
}) {
  return (
    <div className="flex items-center justify-between py-3">
      <div className="flex items-center gap-2.5 text-muted-foreground">
        <Icon className="size-4" />
        <span className="text-sm">{label}</span>
      </div>
      <span className={cn("text-sm font-medium text-foreground", valueClassName)}>{value}</span>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  variant = "default",
}: {
  label: string;
  value: string;
  icon: React.ElementType;
  variant?: "default" | "success" | "warning" | "danger";
}) {
  return (
    <div
      className={cn(
        "flex flex-col gap-1.5 rounded-lg border p-4",
        variant === "success" && "border-emerald-500/20 bg-emerald-500/10",
        variant === "warning" && "border-amber-500/20 bg-amber-500/10",
        variant === "danger" && "border-red-500/20 bg-red-500/10",
        variant === "default" && "border-border bg-muted/30",
      )}
    >
      <div className="flex items-center gap-2">
        <Icon
          className={cn(
            "size-4",
            variant === "success" && "text-emerald-600",
            variant === "warning" && "text-amber-600",
            variant === "danger" && "text-red-500",
            variant === "default" && "text-muted-foreground",
          )}
        />
        <span
          className={cn(
            "text-xs font-medium",
            variant === "success" && "text-emerald-700",
            variant === "warning" && "text-amber-700",
            variant === "danger" && "text-red-600",
            variant === "default" && "text-muted-foreground",
          )}
        >
          {label}
        </span>
      </div>
      <span
        className={cn(
          "text-xl font-bold tabular-nums",
          variant === "success" && "text-emerald-600",
          variant === "warning" && "text-amber-600",
          variant === "danger" && "text-red-500",
          variant === "default" && "text-foreground",
        )}
      >
        {value}
      </span>
    </div>
  );
}

export function TransactionSummary({
  isPurchaseType,
  className,
  points,
  pointsToEarn,
  pointsToSpend,
  coveredValue,
  pointsToRewards,
  cashValue,
  purchaseValue,
}: TransactionSummaryProps) {
  const { t } = useTranslation();
  const netChange = points;
  const hasPartialCoverage = !!cashValue && !!coveredValue;
  const hasFullCoverage = !!coveredValue && !cashValue;
  const needsCashOnly = !coveredValue && !!cashValue;

  return (
    <div className={cn("space-y-4", className)}>
      {/* Points Summary Cards */}
      <div className="grid grid-cols-2 gap-3">
        {pointsToEarn > 0 && (
          <StatCard
            label={t("transactions.summary.earned")}
            value={`+${pointsToEarn.toLocaleString()}`}
            icon={TrendingUp}
            variant="success"
          />
        )}
        {pointsToRewards > 0 && (
          <StatCard
            label={t("transactions.summary.redeemed")}
            value={`-${pointsToRewards.toLocaleString()}`}
            icon={TrendingDown}
            variant="danger"
          />
        )}
        {isPurchaseType && (
          <StatCard
            label={t("transactions.summary.net_change")}
            value={netChange >= 0 ? `+${netChange.toLocaleString()}` : `${netChange.toLocaleString()}`}
            icon={netChange >= 0 ? TrendingUp : TrendingDown}
            variant={netChange >= 0 ? "success" : "danger"}
          />
        )}
      </div>

      {/* Payment Status Banner — only relevant for direct spending (redemptions) */}
      {!isPurchaseType && hasFullCoverage && (
        <div className="flex items-center gap-3 rounded-lg bg-emerald-500/10 px-4 py-3">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-emerald-500/20">
            <CheckCircle2 className="size-4 text-emerald-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-emerald-700">{t("transactions.details.fully_covered")}</p>
            <p className="text-xs text-emerald-600">{coveredValue} {t("transactions.details.saved")}</p>
          </div>
        </div>
      )}

      {!isPurchaseType && hasPartialCoverage && (
        <div className="flex items-center gap-3 rounded-lg bg-amber-500/10 px-4 py-3">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-amber-500/20">
            <AlertCircle className="size-4 text-amber-600" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-amber-700">{t("transactions.details.partial_coverage")}</p>
            <p className="text-xs text-amber-600">
              {coveredValue} {t("transactions.details.covered")} &middot; {cashValue} {t("transactions.details.cash")}
            </p>
          </div>
        </div>
      )}

      {!isPurchaseType && needsCashOnly && (
        <div className="flex items-center gap-3 rounded-lg bg-red-500/10 px-4 py-3">
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-red-500/20">
            <Wallet className="size-4 text-red-500" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-red-600">{t("transactions.details.cash_required")}</p>
            <p className="text-xs text-red-500">{cashValue}</p>
          </div>
        </div>
      )}

      {/* Transaction Details */}
      <div>
        <SectionHeader title={t("transactions.details.transaction_details")} />
        <div className="divide-y divide-border/50">
          {!!purchaseValue && (
            <InfoRow
              icon={CreditCard}
              label={t("transactions.details.purchase_amount")}
              value={purchaseValue}
              valueClassName="font-semibold"
            />
          )}
          {!!coveredValue && (
            <InfoRow
              icon={Coins}
              label={t("transactions.details.covered_by_points")}
              value={`-${coveredValue}`}
              valueClassName="text-emerald-600 font-semibold"
            />
          )}
          {!!cashValue && (
            <InfoRow
              icon={Wallet}
              label={t("transactions.details.cash_payment")}
              value={cashValue}
              valueClassName="text-amber-600 font-semibold"
            />
          )}
        </div>
      </div>
    </div>
  );
}
